import {
    displaceSvgContent,
    type NoiseDisplacementStyle,
} from "./displacementMap";
import { extractSvgInnerContent, parseViewBoxParts } from "./channelSplitVector";

export type DistortionAlgorithm = NoiseDisplacementStyle;

export interface DistortionGridParams {
    svgContent: string;
    cols: number;
    rows: number;
    maxStrength: number;
    algorithm: DistortionAlgorithm;
    seed: string;
    noiseScale: number;
    octaves: number;
    circleSegments: number;
}

function fmt(n: number): string {
    return parseFloat(n.toFixed(4)).toString();
}

export function buildDistortionGridSvg(params: DistortionGridParams): string {
    const {
        svgContent,
        cols,
        rows,
        maxStrength,
        algorithm,
        seed,
        noiseScale,
        octaves,
        circleSegments,
    } = params;

    const vbMatch = svgContent.match(/viewBox=["']([^"']+)["']/);
    const vbStr = vbMatch?.[1] ?? "0 0 100 100";
    const vb = parseViewBoxParts(vbStr) ?? { x: 0, y: 0, width: 100, height: 100 };

    const totalCells = cols * rows;
    const clipDefs: string[] = [];
    const cellGroups: string[] = [];

    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
            const i = row * cols + col;
            const strength = totalCells > 1 ? maxStrength * i / (totalCells - 1) : 0;

            const displaced = strength <= 0
                ? svgContent
                : displaceSvgContent(svgContent, {
                    strength,
                    circleSegments,
                    mode: "noise",
                    seed,
                    noiseScale,
                    octaves,
                    noiseStyle: algorithm,
                    imageData: null,
                    channelMode: "rg",
                    svgMinX: vb.x,
                    svgMinY: vb.y,
                    svgWidth: vb.width,
                    svgHeight: vb.height,
                });

            const innerMarkup = extractSvgInnerContent(displaced);

            // Clip rect is in output coordinate space (no transform on outer <g>)
            const clipId = `dg-c-${row}-${col}`;
            const clipX = fmt(col * vb.width);
            const clipY = fmt(row * vb.height);
            clipDefs.push(
                `<clipPath id="${clipId}"><rect x="${clipX}" y="${clipY}" width="${fmt(vb.width)}" height="${fmt(vb.height)}"/></clipPath>`
            );

            // Inner <g> shifts source coords (vb.x, vb.y) to cell origin (col*W, row*H)
            const tx = fmt(col * vb.width - vb.x);
            const ty = fmt(row * vb.height - vb.y);
            cellGroups.push(
                `<g clip-path="url(#${clipId})"><g transform="translate(${tx},${ty})">${innerMarkup}</g></g>`
            );
        }
    }

    const totalW = fmt(cols * vb.width);
    const totalH = fmt(rows * vb.height);

    return [
        `<svg viewBox="0 0 ${totalW} ${totalH}" xmlns="http://www.w3.org/2000/svg">`,
        `<defs>`,
        ...clipDefs,
        `</defs>`,
        ...cellGroups,
        `</svg>`,
    ].join("\n");
}
